import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Grid,
  Chip,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

const AddClientPage = () => {
  // Basic client information (available to all tiers)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: ''
  });
  
  // Additional information (now available to all users)
  const [additionalData, setAdditionalData] = useState({
    address: '',
    city: '',
    state: '',
    zip_code: '',
    date_of_birth: '',
    emergency_contact: '',
    emergency_phone: ''
  });
  
  // Advanced profile fields (Standard and higher tiers)
  const [advancedData, setAdvancedData] = useState({
    preferred_styles: [], // Array of style preferences
    allergies: '',
    medical_conditions: '',
    referral_source: '',
    notes: ''
  });
  
  // Style preferences management
  const [newStyle, setNewStyle] = useState('');
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const api = UseApi();
  const { 
    isBasicSubscriber,
    isStandardSubscriber, 
    isGrowthStudioSubscriber,
    getSubscriptionLevel 
  } = UseAuthRoles();
  
  // Check if user has access to advanced profile features (Standard or Growth Studio only)
  // Note: Starter Studio gets basic client profile - they need Growth Studio for advanced features
  const hasAdvancedAccess = isStandardSubscriber || isGrowthStudioSubscriber;
  
  // Handle basic form changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  // Handle additional data form changes
  const handleAdditionalChange = (e) => {
    setAdditionalData({ ...additionalData, [e.target.name]: e.target.value });
  };
  
  // Handle advanced data form changes
  const handleAdvancedChange = (e) => {
    setAdvancedData({ ...advancedData, [e.target.name]: e.target.value });
  };
  
  // Style preferences management
  const handleAddStyle = () => {
    if (newStyle.trim() && !advancedData.preferred_styles.includes(newStyle.trim())) {
      setAdvancedData({
        ...advancedData,
        preferred_styles: [...advancedData.preferred_styles, newStyle.trim()]
      });
      setNewStyle('');
    }
  };
  
  const handleRemoveStyle = (styleToRemove) => {
    setAdvancedData({
      ...advancedData,
      preferred_styles: advancedData.preferred_styles.filter(style => style !== styleToRemove)
    });
  };
  
  const handleStyleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStyle();
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Combine data based on subscription level
      let clientData = { 
        ...formData,
        ...additionalData // Now available to all users
      };
      
      // Clean up date_of_birth - remove if empty or invalid
      if (!clientData.date_of_birth || clientData.date_of_birth.trim() === '') {
        delete clientData.date_of_birth;
      } else {
        // Validate date format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(clientData.date_of_birth)) {
          delete clientData.date_of_birth;
        }
      }
      
      // Clean up other empty fields to avoid validation errors
      Object.keys(clientData).forEach(key => {
        if (clientData[key] === '' || clientData[key] === null || clientData[key] === undefined) {
          delete clientData[key];
        }
      });
      
      // Add advanced data if user has standard or higher subscription
      if (hasAdvancedAccess) {
        const cleanAdvancedData = { ...advancedData };
        
        // Clean up empty advanced fields
        Object.keys(cleanAdvancedData).forEach(key => {
          if (key !== 'preferred_styles' && (cleanAdvancedData[key] === '' || cleanAdvancedData[key] === null || cleanAdvancedData[key] === undefined)) {
            delete cleanAdvancedData[key];
          }
        });
        
        clientData = { 
          ...clientData, 
          ...cleanAdvancedData,
          // Convert preferred_styles array to comma-separated string for backend
          preferred_styles: advancedData.preferred_styles.join(', ')
        };
        
        // Remove preferred_styles if empty
        if (!clientData.preferred_styles || clientData.preferred_styles.trim() === '') {
          delete clientData.preferred_styles;
        }
      }
      
      // Add client
      await api.addClient(clientData);
      setSuccess(true);
      
      // Redirect to clients page after short delay
      setTimeout(() => {
        navigate('/clients');
      }, 1500);
    } catch (error) {
      console.error('Error adding client:', error);
      setError(`Failed to add client: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Get current subscription level
  const subscriptionLevel = getSubscriptionLevel();
  
  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>Add New Client</Typography>
          <Typography variant="body2" color="textSecondary">
            Current subscription: {subscriptionLevel.charAt(0).toUpperCase() + subscriptionLevel.slice(1).replace('_', ' ')}
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Client added successfully! Redirecting...
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* Basic Information - All Tiers */}
          <Typography variant="h6" gutterBottom>Basic Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Additional Information - Now Available to All Users */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={additionalData.address}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={additionalData.city}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={additionalData.state}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="zip_code"
                  value={additionalData.zip_code}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={additionalData.date_of_birth}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  name="emergency_contact"
                  value={additionalData.emergency_contact}
                  onChange={handleAdditionalChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Emergency Phone"
                  name="emergency_phone"
                  value={additionalData.emergency_phone}
                  onChange={handleAdditionalChange}
                />
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Advanced Profile Information - Standard and higher tiers */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Advanced Client Profile
              {!hasAdvancedAccess && (
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  (Standard or Growth Studio subscription required)
                </Typography>
              )}
            </Typography>
            
            <Grid container spacing={2}>
              {/* Style Preferences */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Tattoo Style Preferences
                </Typography>
                <TextField
                  fullWidth
                  label="Add Style Preference"
                  value={newStyle}
                  onChange={(e) => setNewStyle(e.target.value)}
                  onKeyPress={handleStyleInputKeyPress}
                  disabled={!hasAdvancedAccess}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddStyle}
                          disabled={!newStyle.trim() || !hasAdvancedAccess}
                          edge="end"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
                
                {/* Display added styles */}
                {advancedData.preferred_styles.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {advancedData.preferred_styles.map((style, index) => (
                      <Chip
                        key={index}
                        label={style}
                        onDelete={
                          hasAdvancedAccess 
                            ? () => handleRemoveStyle(style)
                            : undefined
                        }
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Referral Source"
                  name="referral_source"
                  value={advancedData.referral_source}
                  onChange={handleAdvancedChange}
                  disabled={!hasAdvancedAccess}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Allergies"
                  name="allergies"
                  value={advancedData.allergies}
                  onChange={handleAdvancedChange}
                  multiline
                  rows={3}
                  disabled={!hasAdvancedAccess}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Medical Conditions"
                  name="medical_conditions"
                  value={advancedData.medical_conditions}
                  onChange={handleAdvancedChange}
                  multiline
                  rows={3}
                  disabled={!hasAdvancedAccess}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  name="notes"
                  value={advancedData.notes}
                  onChange={handleAdvancedChange}
                  multiline
                  rows={4}
                  disabled={!hasAdvancedAccess}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Submit Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/clients')}
              disabled={loading}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Adding Client...' : 'Add Client'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AddClientPage;